// Icon resolution for normalized activity rows.
//
// Mobile resolves icons with RN SVG component factories (returnCoinIcon etc.)
// that don't port. The PWA matches against the imported-asset lookup arrays in
// constants/constants.ts instead — this module is the single implementation of
// that matching, which previously existed as four near-identical inline copies
// (Activitylist ×2, RecentTransaction, RecentTnxn).

import { coins, electricUtils, utils } from "../../constants/constants";
import type { ActivityIconMeta } from "./normalizeTransaction";

/**
 * CRA asset imports are plain strings under webpack 5, but the existing code
 * defensively handled `{ src }` / `{ default }` wrappers. Kept for parity.
 */
export const logoSrc = (logo: unknown): string => {
  if (typeof logo === "string") return logo;
  const asObject = logo as { src?: string; default?: string } | undefined;
  return asObject?.src ?? asObject?.default ?? "";
};

export const resolveCoinLogo = (abbr?: string): string => {
  if (!abbr) return "";
  const upper = abbr.toUpperCase();
  const match = coins.find(
    (c) => c.abbr?.toUpperCase() === upper ||
      (c.abbr === "POL" && upper === "MATIC")
  );
  return logoSrc(match?.logo);
};

/** Mobile networks (MTN, Airtel, Glo, …) matched on the lowercase `abbr`. */
export const resolveNetworkLogo = (network?: string): string => {
  if (!network) return "";
  const needle = network.toLowerCase();
  const match = utils.find(
    (u) => u.abbr && (needle.includes(u.abbr.toLowerCase()) || u.abbr.toLowerCase().includes(needle))
  );
  return logoSrc(match?.logo);
};

/**
 * Electricity discos arrive under several names ("Ikeja Electric",
 * "IKEDC", "ikeja-electric"), so match on word overlap against the disco's
 * full name — the same fuzzy approach the old inline code used.
 */
export const resolveElectricityLogo = (disco?: string): string => {
  if (!disco) return "";
  const words = disco
    .toLowerCase()
    .split(/[\s()\-_]+/)
    .filter(Boolean);
  if (!words.length) return "";
  const match = electricUtils.find(
    (utility) =>
      words.some((word) => utility.name?.toLowerCase().includes(word)) ||
      words.some((word) => utility.serviceId?.toLowerCase().includes(word))
  );
  return logoSrc(match?.logo);
};

/**
 * Returns an image URL for a row, or "" when nothing matches — callers fall
 * back to a themed react-icon (the bolt for electricity, a phone for airtime/
 * data, a controller for betting, a gift for gift cards).
 */
export const resolveActivityIcon = (iconMeta: ActivityIconMeta): string => {
  switch (iconMeta.kind) {
    case "coin":
    case "bank":
      return resolveCoinLogo(iconMeta.value);
    case "utility":
      return iconMeta.isElectricity
        ? resolveElectricityLogo(iconMeta.value)
        : resolveNetworkLogo(iconMeta.value);
    case "giftcard":
    default:
      return "";
  }
};
